import React from 'react';
import { ThemeProvider } from 'next-themes';
import Layout from './components/Layout';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Tenders from './pages/Tenders';
import TenderDetail from './pages/TenderDetail';
import KnowledgeBase from './pages/KnowledgeBase';
import VoiceLab from './pages/VoiceLab';
import Campaigns from './pages/Campaigns';
import Sources from './pages/Sources';
import Settings from './pages/Settings';
import ZATCASettings from './pages/ZATCASettings';
import Clients from './pages/Clients';
import Contacts from './pages/Contacts';
import Leads from './pages/Leads';
import Deals from './pages/Deals';
import Tasks from './pages/Tasks';
import Tickets from './pages/Tickets';
import Invoices from './pages/Invoices';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import LandingPage from './pages/LandingPage';
import RetailLandingPage from './pages/RetailLandingPage';
import ComparisonPage from './pages/ComparisonPage';
// CRM Lite - Business Partners for Finance users
import BusinessPartners from './pages/BusinessPartners';
// Accounts Module
import ChartOfAccounts from './pages/ChartOfAccounts';
import JournalEntries from './pages/JournalEntries';
import FinancialReports from './pages/FinancialReports';
import BankAccounts from './pages/BankAccounts';
// HR Module
import Employees from './pages/Employees';
import Departments from './pages/Departments';
import Attendance from './pages/Attendance';
import Payroll from './pages/Payroll';
import LeaveManagement from './pages/LeaveManagement';
import PerformanceReviews from './pages/PerformanceReviews';
// Procurement Module
import Suppliers from './pages/Suppliers';
import Products from './pages/Products';
import PurchaseOrders from './pages/PurchaseOrders';
import GoodsReceipt from './pages/GoodsReceipt';
import PurchaseBills from './pages/PurchaseBills';
import SupplierPayments from './pages/SupplierPayments';
import PurchaseReturns from './pages/PurchaseReturns';
import PurchaseReports from './pages/PurchaseReports';
// Sales Module
import Quotations from './pages/Quotations';
import SalesOrders from './pages/SalesOrders';
// Inventory Module
import Warehouses from './pages/Warehouses';
// Document Management
import Documents from './pages/Documents';
import DocumentTemplates from './pages/DocumentTemplates';
// Currency Management
import Currencies from './pages/Currencies';
// System
import SystemStatus from './pages/SystemStatus';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import { Toaster } from 'sonner';

// Customer Portal
import PortalLogin from './pages/portal/PortalLogin';
import PortalLayout from './pages/portal/PortalLayout';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalInvoices from './pages/portal/PortalInvoices';
import PortalQuotations from './pages/portal/PortalQuotations';
import PortalProjects from './pages/portal/PortalProjects';
import PortalTickets from './pages/portal/PortalTickets';
import PortalDocuments from './pages/portal/PortalDocuments';

// POS Terminal (Standalone)
import POSLogin from './pages/pos/POSLogin';
import POSLayout from './pages/pos/POSLayout';
import POSTerminal from './pages/pos/POSTerminal';
import GasStationPOS from './pages/pos/GasStationPOS';
import GasStationDemo from './pages/pos/GasStationDemo';
import POSUserManagement from './pages/POSUserManagement';

// Gas Station Marketing
import GasStationLanding from './pages/GasStationLanding';

// Manual Tank Entry
import ManualTankEntry from './pages/ManualTankEntry';

// Live Pump Monitor
import LivePumpMonitor from './pages/LivePumpMonitor';

// Multi-Store Dashboard
import MultiStoreDashboard from './pages/MultiStoreDashboard';

// Store Analytics
import StoreAnalytics from './pages/StoreAnalytics';

// Compliance Reports
import ComplianceReports from './pages/ComplianceReports';

// Public Tools (SEO)
import TobaccoTaxCalculator from './pages/TobaccoTaxCalculator';
import ProfitMarginCalculator from './pages/ProfitMarginCalculator';
import AgeVerificationChecker from './pages/AgeVerificationChecker';

// Inventory Transfers
import InventoryTransfers from './pages/InventoryTransfers';

// Subscription & Pricing
import PricingPage from './pages/PricingPage';
import AppStore from './pages/AppStore';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SecurityPage from './pages/SecurityPage';
import CookiePolicy from './pages/CookiePolicy';

// Logo Preview
import LogoPreview from './pages/LogoPreview';

// Project Management (Enhanced with Sites, Scopes, POs, Invoices, Payments)
import Sites from './pages/Sites';
import SiteDetail from './pages/SiteDetail';
import ProjectsList from './pages/Jobs';  // Renamed from Jobs
import ProjectDetail from './pages/JobDetail';  // Renamed from JobDetail

// Blog Management
import BlogAdmin from './pages/BlogAdmin';
import PublicBlog, { BlogArticle } from './pages/PublicBlog';

// Master Admin (White-Label Management)
import MasterAdminDashboard from './pages/MasterAdminDashboard';

// Featured Page (Investor Presentation)
import FeaturedPage from './pages/FeaturedPage';

// Email Settings
import EmailSettings from './pages/EmailSettings';

// Multi-Company Management
import CompanyManagement from './pages/CompanyManagement';

import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { ModuleAccessProvider, useModuleAccess } from './context/ModuleAccessContext';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Module-protected route - checks if user has access to the module
const ModuleProtectedRoute = ({ children, moduleId }) => {
  const { user, loading } = useAuth();
  const { canAccessModule, loading: moduleLoading, plan_name } = useModuleAccess();
  
  if (loading || moduleLoading) {
    return <div className="h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) return <Navigate to="/login" replace />;
  
  // Check module access
  if (!canAccessModule(moduleId)) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-background">
        <div className="text-center max-w-md p-8 rounded-2xl bg-card border shadow-lg">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6V4a2 2 0 00-2-2H8a2 2 0 00-2 2v7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">Upgrade Required</h1>
          <p className="text-muted-foreground mb-4">
            This feature is not included in your current plan ({plan_name}).
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Upgrade your subscription to unlock this module and many more features.
          </p>
          <div className="flex gap-3 justify-center">
            <a href="/dashboard" className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              Go to Dashboard
            </a>
            <a href="/settings" className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90">
              Upgrade Plan
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

// Admin-only route protection
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return <div className="h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  
  // Check if user has admin role
  if (user.role !== 'admin') {
    // Redirect non-admins to dashboard
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
        <p className="text-muted-foreground mb-4">This page is only accessible to the platform administrator.</p>
        <a href="/dashboard" className="text-primary hover:underline">Go to Dashboard</a>
      </div>
    );
  }
  
  return children;
};

// RTL Direction Handler
const RTLHandler = ({ children }) => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Set document direction and lang based on current language
    const isArabic = i18n.language === 'ar';
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
    
    // Add/remove Arabic font class
    if (isArabic) {
      document.documentElement.classList.add('font-arabic');
    } else {
      document.documentElement.classList.remove('font-arabic');
    }
  }, [i18n.language]);
  
  return children;
};

function App() {
  const location = useLocation();
  const isAuthPage = ['/login', '/signup'].includes(location.pathname);
  const isLanding = location.pathname === '/';
  
  // Check if white-label should skip landing page
  const skipLanding = process.env.REACT_APP_SKIP_LANDING === 'true';

  return (
    <AuthProvider>
      <ModuleAccessProvider>
        <SettingsProvider>
          <RTLHandler>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <Routes>
            {/* Landing or redirect to login based on white-label config */}
            <Route path="/" element={skipLanding ? <Navigate to="/login" replace /> : <RetailLandingPage />} />
            <Route path="/comparison" element={skipLanding ? <Navigate to="/login" replace /> : <ComparisonPage />} />
            <Route path="/compare" element={skipLanding ? <Navigate to="/login" replace /> : <ComparisonPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/tools/tobacco-tax-calculator" element={<TobaccoTaxCalculator />} />
          <Route path="/tools/profit-margin-calculator" element={<ProfitMarginCalculator />} />
          <Route path="/tools/age-verification-checker" element={<AgeVerificationChecker />} />
          <Route path="/pricing" element={<PricingPage />} />
          
          {/* Legal Pages - Public */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          
          {/* Logo Preview - Public */}
          <Route path="/logo-preview" element={<LogoPreview />} />
          
          {/* Public Blog Routes */}
          <Route path="/blog" element={<PublicBlog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          
          {/* Customer Portal Routes */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/*" element={
            <PortalLayout>
              <Routes>
                <Route path="/dashboard" element={<PortalDashboard />} />
                <Route path="/invoices" element={<PortalInvoices />} />
                <Route path="/quotations" element={<PortalQuotations />} />
                <Route path="/projects" element={<PortalProjects />} />
                <Route path="/tickets" element={<PortalTickets />} />
                <Route path="/documents" element={<PortalDocuments />} />
              </Routes>
            </PortalLayout>
          } />
          
          {/* POS Terminal Routes (Standalone - New Window) */}
          <Route path="/pos-login" element={<POSLogin />} />
          <Route path="/pos-terminal" element={
            <ProtectedRoute>
              <POSTerminal />
            </ProtectedRoute>
          } />
          
          {/* POS Route - Protected Full Screen POS */}
          <Route path="/pos" element={
            <ProtectedRoute>
              <POSTerminal />
            </ProtectedRoute>
          } />

          {/* Gas Station POS - Fuel Pump Control */}
          <Route path="/pos/gas-station" element={
            <ProtectedRoute>
              <GasStationPOS />
            </ProtectedRoute>
          } />

          {/* Gas Station Demo - Public Demo Mode */}
          <Route path="/pos/gas-station-demo" element={<GasStationDemo />} />

          {/* Gas Station Landing/Marketing Page */}
          <Route path="/gas-station" element={<GasStationLanding />} />
          
          {/* Protected Routes with Layout */}
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/tenders" element={<Tenders />} />
                  <Route path="/tenders/:id" element={<TenderDetail />} />
                  <Route path="/knowledge" element={<KnowledgeBase />} />
                  <Route path="/voice-lab" element={<VoiceLab />} />
                  <Route path="/campaigns" element={<Campaigns />} />
                  <Route path="/sources" element={<Sources />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/leads" element={<Leads />} />
                  <Route path="/deals" element={<Deals />} />
                  <Route path="/projects" element={<ProjectsList />} />
                  <Route path="/projects/sites" element={<Sites />} />
                  <Route path="/projects/sites/:siteId" element={<SiteDetail />} />
                  <Route path="/projects/:jobId" element={<ProjectDetail />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/tickets" element={<Tickets />} />
                  
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/business-partners" element={<BusinessPartners />} />
                  <Route path="/chart-of-accounts" element={<ChartOfAccounts />} />
                  <Route path="/journal-entries" element={<JournalEntries />} />
                  <Route path="/financial-reports" element={<FinancialReports />} />
                  <Route path="/bank-accounts" element={<BankAccounts />} />
                  <Route path="/employees" element={<Employees />} />
                  <Route path="/departments" element={<Departments />} />
                  <Route path="/attendance" element={<Attendance />} />
                  <Route path="/payroll" element={<Payroll />} />
                  <Route path="/leave" element={<LeaveManagement />} />
                  <Route path="/performance" element={<PerformanceReviews />} />
                  <Route path="/suppliers" element={<Suppliers />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/purchase-orders" element={<PurchaseOrders />} />
                  <Route path="/goods-receipt" element={<GoodsReceipt />} />
                  <Route path="/purchase-bills" element={<PurchaseBills />} />
                  <Route path="/supplier-payments" element={<SupplierPayments />} />
                  <Route path="/purchase-returns" element={<PurchaseReturns />} />
                  <Route path="/purchase-reports" element={<PurchaseReports />} />
                  <Route path="/quotations" element={<Quotations />} />
                  <Route path="/sales-orders" element={<SalesOrders />} />
                  <Route path="/warehouses" element={<Warehouses />} />
                  <Route path="/documents" element={<Documents />} />
                  <Route path="/document-templates" element={<DocumentTemplates />} />
                  <Route path="/currencies" element={<Currencies />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/pos-users" element={<POSUserManagement />} />
                  <Route path="/marketplace" element={<AppStore />} />
                  <Route path="/system-status" element={<AdminRoute><SystemStatus /></AdminRoute>} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/zatca" element={<ZATCASettings />} />
                  <Route path="/settings/email" element={<EmailSettings />} />
                  <Route path="/companies" element={<AdminRoute><CompanyManagement /></AdminRoute>} />
                  <Route path="/blog-admin" element={<BlogAdmin />} />
                  <Route path="/master-admin" element={<AdminRoute><MasterAdminDashboard /></AdminRoute>} />
                  <Route path="/featured" element={<AdminRoute><FeaturedPage /></AdminRoute>} />
                  <Route path="/tank-readings" element={<ManualTankEntry />} />
                  <Route path="/pump-monitor" element={<LivePumpMonitor />} />
                  <Route path="/stores" element={<MultiStoreDashboard />} />
                  <Route path="/store-analytics" element={<StoreAnalytics />} />
                  <Route path="/inventory-transfers" element={<InventoryTransfers />} />
                  <Route path="/compliance" element={<ComplianceReports />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster theme="system" position="bottom-right" />
        </ThemeProvider>
        </RTLHandler>
        </SettingsProvider>
      </ModuleAccessProvider>
    </AuthProvider>
  );
}

export default App;
